/**
 * @file CookieConsentBanner.tsx
 * @description Modern institutional Cookie Consent Banner with customization options
 * and seamless integration with the Legal & Cookie Policy Modal.
 */
import React, { useState, useEffect } from 'react';
import { CookieRounded, ShieldRounded, CloseRounded } from '@mui/icons-material';
import { Button } from './ui/index.js';

interface CookieConsentBannerProps {
  onOpenCookiePolicy?: () => void;
}

const STORAGE_KEY = 'forex_builder_cookie_consent_v1';

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onOpenCookiePolicy }) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      // Delay showing slightly for clean initial page render
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: 'all', timestamp: Date.now() }));
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: 'essential', timestamp: Date.now() }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: 'calc(100% - 32px)',
        maxWidth: '920px',
        background: 'rgba(18, 22, 30, 0.96)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid var(--bybit-border-light)',
        borderRadius: 'var(--radius-lg)',
        padding: '20px 24px',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7), 0 0 24px rgba(240, 185, 11, 0.12)',
        animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          flexWrap: 'wrap',
        }}
      >
        {/* Left Content */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flex: 1, minWidth: '280px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(240, 185, 11, 0.12)',
              border: '1px solid var(--bybit-brand-primary)',
              color: 'var(--bybit-brand-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 0 16px rgba(240, 185, 11, 0.25)',
            }}
          >
            <CookieRounded style={{ width: 22, height: 22 }} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.9375rem', letterSpacing: '-0.2px' }}>
                We Value Your Privacy &amp; Data Security
              </span>
              <span className="bybit-badge-gold" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                GDPR / CCPA
              </span>
            </div>
            <p
              style={{
                color: 'var(--bybit-text-secondary)',
                fontSize: '0.8125rem',
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              We use essential cookies to maintain secure sessions and local storage to preserve your strategy drafts. We also use anonymized telemetry to optimize backtesting speed.{' '}
              <button
                type="button"
                onClick={onOpenCookiePolicy}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--bybit-brand-primary)',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.8125rem',
                }}
              >
                Learn More &amp; Cookie Settings
              </button>
            </p>
          </div>
        </div>

        {/* Right Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleAcceptEssential}
            style={{
              padding: '9px 16px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            Essential Only
          </Button>

          <Button
            type="button"
            onClick={handleAcceptAll}
            style={{
              padding: '9px 20px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(240, 185, 11, 0.3)',
            }}
          >
            Accept All Cookies
          </Button>

          <button
            type="button"
            onClick={handleAcceptEssential}
            title="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--bybit-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
            }}
          >
            <CloseRounded style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );
};
